import {
  useEditor,
  EditorContent,
  type Editor as TipTapEditor,
} from "@tiptap/react";
import {
  useCallback,
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
} from "react";
import type { SuggestionOptions } from "@tiptap/suggestion";
import { toast } from "sonner";
import styles from "./Editor.module.scss";
import { useEditorExtensions } from "./hooks/useEditorExtensions";
import { useEditorContentSync } from "./hooks/useEditorContentSync";
import { useLinkEditor } from "./hooks/useLinkEditor";
import { useBubbleMenuController } from "./hooks/useBubbleMenuController";
import { useEditorFormattingState } from "./hooks/useEditorFormattingState";
import { EditorBubbleMenu } from "./components/EditorBubbleMenu";
import { EditorToolbar } from "./components/EditorToolbar";
import { useUploadSnippetAttachment } from "@/v2/entities/KnowledgeSnippet";
import { validateMediaFile } from "./utils/fileValidation";

interface EditorProps {
  content?: string;
  onChange?: (content: string) => void;
  editable?: boolean;
  showMarkdownToggle?: boolean;
  showToolbar?: boolean;
  snippetId?: string;
  className?: string;
  enableMentions?: boolean;
  mentionOptions?: Omit<SuggestionOptions, "editor">;
  placeholder?: string;
  appendTo?: () => HTMLElement;
}

export interface EditorRef {
  insertImage: (url: string) => void;
  insertVideo: (url: string) => void;
}

// Internal component that actually renders the editor
const EditorCore = forwardRef<EditorRef, EditorProps>(
  (
    {
      content = "",
      onChange,
      editable = true,
      showMarkdownToggle = false,
      showToolbar = false,
      snippetId,
      className,
      enableMentions = false,
      mentionOptions,
      placeholder,
      appendTo,
    },
    ref
  ) => {
    // const content = ContentRaw.replace(/\n/g, "<br>");

    const [showMarkdown, setShowMarkdown] = useState(false);

    // Hook: Upload attachment mutation (only if snippetId is provided)
    const { mutateAsync: uploadAttachment } = useUploadSnippetAttachment();

    const uploadAndInsertMedia = useCallback(
      async (currentEditor: any, file: File, pos?: number) => {
        if (!snippetId) {
          return;
        }

        const validation = validateMediaFile(file);
        if (!validation.valid) {
          toast.error(validation.error);
          return;
        }

        try {
          const uploadPromise = uploadAttachment({
            snippetId,
            file,
          });
          toast.promise(uploadPromise, {
            loading: `Uploading ${validation.mediaType}…`,
            success: `${
              validation.mediaType === "image" ? "Image" : "Video"
            } uploaded successfully`,
            error: `Failed to upload ${validation.mediaType}. Please try again.`,
          });

          const publicUrl = await uploadPromise;

          if (validation.mediaType === "image") {
            if (typeof pos === "number") {
              currentEditor
                .chain()
                .focus()
                .insertContentAt(pos, {
                  type: "image",
                  attrs: { src: publicUrl },
                })
                .run();
            } else {
              currentEditor.chain().focus().setImage({ src: publicUrl }).run();
            }
          } else if (validation.mediaType === "video") {
            if (typeof pos === "number") {
              currentEditor
                .chain()
                .focus()
                .insertContentAt(pos, {
                  type: "video",
                  attrs: { src: publicUrl },
                })
                .run();
            } else {
              currentEditor.chain().focus().setVideo({ src: publicUrl }).run();
            }
          }
        } catch (error) {
          console.error("Failed to upload file:", error);
        }
      },
      [snippetId, uploadAttachment]
    );

    const handleFileDrop = useCallback(
      async (currentEditor: any, files: File[], pos: number) => {
        if (!snippetId) {
          return;
        }

        for (const file of files) {
          await uploadAndInsertMedia(currentEditor, file, pos);
        }
      },
      [snippetId, uploadAndInsertMedia]
    );

    const handleFilePaste = useCallback(
      async (currentEditor: any, files: File[]) => {
        if (!snippetId) {
          return;
        }

        for (const file of files) {
          await uploadAndInsertMedia(currentEditor, file);
        }
      },
      [snippetId, uploadAndInsertMedia]
    );

    // Hook: Configure editor extensions with file handlers
    const extensions = useEditorExtensions({
      enableMentions,
      mentionOptions,
      placeholder,
      onFileDrop: handleFileDrop,
      onFilePaste: handleFilePaste,
    });

    const { handleUpdate, registerEditor } = useEditorContentSync({
      content,
      onChange,
    });

    // Create editor instance
    const editor = useEditor({
      extensions,
      content,
      editable,
      editorProps: {
        attributes: {
          class:
            "tiptap prose prose-sm sm:prose lg:prose-lg xl:prose-xl focus:outline-none",
          dir: "auto",
        },
        handleDOMEvents: {
          dragover: (view, event) => {
            const dragEvent = event as DragEvent;
            const dataTransfer = dragEvent.dataTransfer;
            if (
              !dataTransfer ||
              !Array.from(dataTransfer.types).includes("Files")
            ) {
              return false;
            }
            dragEvent.preventDefault();
            dragEvent.stopPropagation();
            dataTransfer.dropEffect = "copy";
            return true;
          },
        },
      },
      onUpdate: handleUpdate,
    });

    const handleEditorDrop = useCallback(
      (event: React.DragEvent<HTMLDivElement>) => {
        if (!editor || !snippetId) {
          return;
        }

        const dataTransfer = event.dataTransfer;
        if (!dataTransfer || dataTransfer.files.length === 0) {
          return;
        }

        const dropPos = editor.view.posAtCoords({
          left: event.clientX,
          top: event.clientY,
        });

        if (!dropPos?.pos) {
          return;
        }

        event.preventDefault();
        event.stopPropagation();

        void handleFileDrop(
          editor,
          Array.from(dataTransfer.files),
          dropPos.pos
        );
      },
      [editor, handleFileDrop, snippetId]
    );

    useEffect(() => {
      registerEditor(editor);
      return () => {
        registerEditor(null);
      };
    }, [editor, registerEditor]);

    // Expose insert methods via ref
    useImperativeHandle(
      ref,
      () => ({
        insertImage: (url: string) => {
          if (editor) {
            editor.chain().focus().setImage({ src: url }).run();
          }
        },
        insertVideo: (url: string) => {
          if (editor) {
            editor.chain().focus().setVideo({ src: url }).run();
          }
        },
      }),
      [editor]
    );

    // Hook: Manage link editor state
    const linkEditor = useLinkEditor({ editor });

    // Hook: Control bubble menu visibility and pointer tracking
    const { shouldShow } = useBubbleMenuController({
      editor,
      isEditingLink: linkEditor.isEditingLink,
    });

    // Hook: Get current formatting state
    const formattingState = useEditorFormattingState({ editor });

    // Update editable state when prop changes
    useEffect(() => {
      if (!editor) return;
      editor.setEditable(editable);
    }, [editor, editable]);

    // Helper to get markdown content
    const getMarkdown = () => {
      if (!editor) return "";
      const markdownStorage = (
        editor.storage as { markdown?: { getMarkdown: () => string } }
      ).markdown;
      return markdownStorage?.getMarkdown() ?? "";
    };

    if (!editor) {
      return null;
    }

    return (
      <div className={`${styles.tiptapContainer} ${className || ""}`}>
        {showMarkdownToggle && (
          <div className={styles.editorHeader}>
            <button
              onClick={() => setShowMarkdown(!showMarkdown)}
              className={showMarkdown ? styles.isActive : ""}
              title="Toggle Markdown View"
            >
              MD
            </button>
          </div>
        )}
        {showToolbar && editable && snippetId && !showMarkdown && (
          <EditorToolbar editor={editor} snippetId={snippetId} />
        )}
        <div className={styles.editorWrapper}>
          {showMarkdown ? (
            <div className={styles.markdownView}>
              <pre>{getMarkdown()}</pre>
            </div>
          ) : (
            <>
              {editable && (
                <EditorBubbleMenu
                  editor={editor}
                  shouldShow={shouldShow}
                  formattingState={formattingState}
                  isEditingLink={linkEditor.isEditingLink}
                  linkUrl={linkEditor.linkUrl}
                  setLinkUrl={linkEditor.setLinkUrl}
                  handleSetLink={linkEditor.handleSetLink}
                  handleCancelLink={linkEditor.handleCancelLink}
                  handleStartEditLink={linkEditor.handleStartEditLink}
                  appendTo={appendTo}
                />
              )}
              <EditorContent editor={editor} onDropCapture={handleEditorDrop} />
            </>
          )}
        </div>
      </div>
    );
  }
);

// Wrapper component that uses key to force remount when extension config changes
export const Editor = forwardRef<EditorRef, EditorProps>((props, ref) => {
  // Create a key that changes when mention configuration changes
  const editorKey = `editor-${props.enableMentions}-${
    props.mentionOptions ? "custom" : "default"
  }`;

  return <EditorCore key={editorKey} {...props} ref={ref} />;
});
